import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { EyeIcon } from "lucide-react";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { RfpItem, RfpSortBy, RfpSortDir, RfpStatus, RfpUser } from "../../types/rfp";
import { createDataTablePagination } from "./DataTablePagination";
import { withScrollableDataTable } from "./withScrollableDataTable";

const myRfpsTablePagination = createDataTablePagination({
  rowsPerPageOptions: [10, 20, 30],
});
const ScrollableDataTable = withScrollableDataTable(DataTable);

type MyRFPsTableProps = {
  readonly rfps: readonly RfpItem[];
  readonly totalRows: number;
  readonly isLoading: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly sortBy: RfpSortBy;
  readonly sortDir: RfpSortDir;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number) => void;
  readonly onSortChange: (sortBy: RfpSortBy, sortDir: RfpSortDir) => void;
  readonly onActionClick: (row: RfpItem) => void;
};

const statusConfig: Record<RfpStatus, { label: string; bg: string; text: string }> = {
  pending: {
    label: "Pending",
    bg: "bg-gray-100 border-gray-200",
    text: "text-gray-700",
  },
  pending_review: {
    label: "Pending Review",
    bg: "bg-amber-50 border-amber-200",
    text: "text-amber-700",
  },
  processing: {
    label: "Processing",
    bg: "bg-blue-50 border-blue-200",
    text: "text-blue-700",
  },
  in_review: {
    label: "In Review",
    bg: "bg-violet-50 border-violet-200",
    text: "text-violet-700",
  },
  completed: {
    label: "Completed",
    bg: "bg-emerald-50 border-emerald-200",
    text: "text-emerald-700",
  },
  error: {
    label: "Error",
    bg: "bg-red-50 border-red-200",
    text: "text-red-700",
  },
};

const fallbackStatusConfig = {
  label: "Unknown",
  bg: "bg-gray-100 border-gray-200",
  text: "text-gray-700",
} as const;

const toTitleCaseStatus = (value: string): string => {
  const normalized = value.trim().replace(/[_-]+/g, " ");
  if (!normalized) {
    return fallbackStatusConfig.label;
  }
  return normalized
    .split(" ")
    .filter(Boolean)
    .map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1).toLowerCase()}`)
    .join(" ");
};

type SortableColumn = TableColumn<RfpItem> & {
  readonly sortField?: RfpSortBy;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatPerson = (user: RfpUser | null): string => {
  if (!user) return "—";
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;
  return user.email ?? "—";
};

const formatProgress = (row: RfpItem): string => {
  const completed = row.progress?.completed ?? 0;
  const total = row.progress?.total ?? 0;
  const percentage = row.progress?.percentage ?? 0;
  const normalizedPercentage = Number.isFinite(percentage) ? percentage.toFixed(2) : "0.00";
  return `${completed}/${total} (${normalizedPercentage}%)`;
};

const StatusBadge = ({ status }: { readonly status: string }) => {
  const config = statusConfig[status as RfpStatus] ?? {
    ...fallbackStatusConfig,
    label: toTitleCaseStatus(status),
  };
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
};

export const MyRFPsTable = ({
  rfps,
  totalRows,
  isLoading,
  page,
  perPage,
  sortBy,
  sortDir,
  onChangePage,
  onChangeRowsPerPage,
  onSortChange,
  onActionClick,
}: MyRFPsTableProps) => {
  const columns: SortableColumn[] = useMemo(
    () => [
      {
        id: "name",
        sortField: "name",
        name: "RFP Name",
        selector: (row) => row.name,
        sortable: true,
        grow: 2,
        cell: (row) => (
          <span className="line-clamp-2 font-medium text-gray-900" title={row.name}>
            {row.name}
          </span>
        ),
      },
      {
        id: "status",
        sortField: "status",
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        width: "170px",
        cell: (row) => <StatusBadge status={row.status} />,
      },
      {
        id: "due_date",
        sortField: "due_date",
        name: "Due Date",
        selector: (row) => row.due_date ?? "",
        sortable: true,
        width: "130px",
        cell: (row) => <span className="text-sm text-gray-700">{formatDate(row.due_date)}</span>,
      },
      {
        id: "progress",
        name: "Progress",
        selector: (row) => formatProgress(row),
        sortable: false,
        width: "170px",
        cell: (row) => <span className="text-sm text-gray-700">{formatProgress(row)}</span>,
      },
      {
        id: "owner",
        name: "Owner",
        selector: (row) => formatPerson(row.owner),
        sortable: false,
        width: "160px",
        cell: (row) => (
          <span className="truncate text-sm text-gray-700" title={formatPerson(row.owner)}>
            {formatPerson(row.owner)}
          </span>
        ),
      },
      {
        id: "created_at",
        sortField: "created_at",
        name: "Created",
        selector: (row) => row.created_at,
        sortable: true,
        width: "120px",
        cell: (row) => <span className="text-sm text-gray-700">{formatDate(row.created_at)}</span>,
      },
      {
        id: "modified_at",
        sortField: "modified_at",
        name: "Modified",
        selector: (row) => row.modified_at,
        sortable: true,
        width: "120px",
        cell: (row) => <span className="text-sm text-gray-700">{formatDate(row.modified_at)}</span>,
      },
      {
        name: "Actions",
        cell: (row) => {
          const isViewDisabled = row.status === "processing" || row.status === "pending" || row.status === "error";
          const tooltipText =
            row.status === "error" ? "Processing failed" : "AI extraction in progress";

          return (
            <div className="group relative inline-flex">
              <button
                type="button"
                className="text-gray-500 transition-colors hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-500"
                onClick={() => {
                  if (isViewDisabled) return;
                  onActionClick(row);
                }}
                aria-label="View RFP"
                disabled={isViewDisabled}
              >
                <EyeIcon className="h-5 w-5" />
              </button>
              {isViewDisabled && (
                <span
                  role="tooltip"
                  className="pointer-events-none absolute -top-2 left-1/2 z-20 w-max max-w-56 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
                >
                  {tooltipText}
                </span>
              )}
            </div>
          );
        },
        style: {
          marginRight: '1rem',
          position: 'relative',
        },
        width: "130px",
        right: true,
      },
    ],
    [onActionClick]
  );

  return (
    <div className="min-h-0 w-full flex-1 overflow-hidden">
      <ScrollableDataTable
        columns={columns}
        data={[...rfps]}
        customStyles={dataTableCustomStyles}
        striped={false}
        progressPending={isLoading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={perPage}
        paginationDefaultPage={page}
        paginationRowsPerPageOptions={[10, 20, 30]}
        paginationComponent={myRfpsTablePagination}
        onChangePage={onChangePage}
        onChangeRowsPerPage={(nextPerPage) => onChangeRowsPerPage(nextPerPage)}
        sortServer
        defaultSortFieldId={sortBy}
        defaultSortAsc={sortDir === "asc"}
        onSort={(column, direction) => {
          const sortField = (column as SortableColumn).sortField;
          if (!sortField) return;
          onSortChange(sortField, direction as RfpSortDir);
        }}
        keyField="id"
        noDataComponent={
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">No RFPs found matching your search.</p>
          </div>
        }
        highlightOnHover
        responsive
      />
    </div>
  );
};
